import {z as zod} from "zod";
import {DAG_DataSourceConnectionStrategy} from "~/domainModels/dag/dataSourceConnectionStrategy";
import {ThirdPartyPlatformIds} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/oauth-callback/-utilities";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {dataResult, errorResult, type Result} from "@vesta/error-handling-remix/result";
import {uniqueUuid} from "@vesta/unique/unique";

// #region Google
export function getGoogleOAuthRedirectUri(websiteBaseUrl: string) {
    const redirectUri = `${websiteBaseUrl}/oauth-callback/${ThirdPartyPlatformIds.google}`;

    return redirectUri;
}

export function getGoogleOAuthUrl({
    connectionAttemptId,
    scopes,
    websiteBaseUrl,
}: {
    connectionAttemptId: Uuid;
    scopes: string;
    websiteBaseUrl: string;
}): Result<string> {
    // TODO: Get this from hades
    const googleClientIdResult = zod.string().safeParse(import.meta.env.GOOGLE_CLIENT_ID);
    if (googleClientIdResult.success === false) {
        return errorResult(new Error(uniqueUuid`fd3505ba-a30c-4f3a-ad93-6636c230382a`));
    }
    const googleClientId = googleClientIdResult.data;

    const redirectUri = getGoogleOAuthRedirectUri(websiteBaseUrl);

    const url = new URL("https://accounts.google.com/o/oauth2/v2/auth");
    url.searchParams.set("scope", scopes);
    url.searchParams.set("client_id", googleClientId);
    url.searchParams.set("response_type", "code");
    url.searchParams.set("redirect_uri", redirectUri);
    url.searchParams.set("prompt", "consent");
    url.searchParams.set("access_type", "offline");
    url.searchParams.set("state", connectionAttemptId);

    return dataResult(url.toString());
}

// #endregion

// #region Facebook
export function getFacebookOAuthRedirectUri(websiteBaseUrl: string) {
    const redirectUri = `${websiteBaseUrl}/oauth-callback/${ThirdPartyPlatformIds.facebook}`;

    return redirectUri;
}

export function getFacebookOAuthUrl({
    connectionAttemptId,
    scopes,
    websiteBaseUrl,
}: {
    connectionAttemptId: Uuid;
    scopes: string;
    websiteBaseUrl: string;
}): Result<string> {
    // TODO: Get this from hades
    const facebookApiVersionResult = zod.string().safeParse(import.meta.env.FACEBOOK_API_VERSION);
    const facebookClientIdResult = zod.string().safeParse(import.meta.env.FACEBOOK_CLIENT_ID);
    if (facebookApiVersionResult.success === false || facebookClientIdResult.success === false) {
        return errorResult(new Error(uniqueUuid`4d7c7605-13bb-4ae5-97c1-208ca0171ced`));
    }
    const facebookApiVersion = facebookApiVersionResult.data;
    const facebookClientId = facebookClientIdResult.data;

    const redirectUri = getFacebookOAuthRedirectUri(websiteBaseUrl);
    const url = new URL(`https://www.facebook.com/${facebookApiVersion}/dialog/oauth`);
    url.searchParams.set("scope", scopes);
    url.searchParams.set("client_id", facebookClientId);
    url.searchParams.set("redirect_uri", redirectUri);
    url.searchParams.set("state", connectionAttemptId);

    return dataResult(url.toString());
}
// #endregion

// #region Flipkart
// TODO: 311 Incomplete
export function getFlipkartOAuthRedirectUri(websiteBaseUrl: string, connectionAttemptId: Uuid) {
    const redirectUri = `${websiteBaseUrl}/oauth-callback/${ThirdPartyPlatformIds.flipkart}?connectionAttemptId=${connectionAttemptId}`;

    return redirectUri;
}

export function getFlipkartOAuthUrl({
    connectionAttemptId,
    scopes,
    websiteBaseUrl,
}: {
    connectionAttemptId: Uuid;
    scopes: string;
    websiteBaseUrl: string;
}): Result<string> {
    // TODO: Get this from hades
    const facebookApiVersionResult = zod.string().safeParse(import.meta.env.FACEBOOK_API_VERSION);
    const facebookClientIdResult = zod.string().safeParse(import.meta.env.FACEBOOK_CLIENT_ID);
    if (facebookApiVersionResult.success === false || facebookClientIdResult.success === false) {
        return errorResult(new Error(uniqueUuid`bc17989d-b6aa-49c0-aa1e-a7a268d2c776`));
    }
    const facebookApiVersion = facebookApiVersionResult.data;
    const facebookClientId = facebookClientIdResult.data;

    const redirectUri = getFacebookOAuthRedirectUri(websiteBaseUrl);
    const url = new URL(`https://www.facebook.com/${facebookApiVersion}/dialog/oauth`);
    url.searchParams.set("scope", scopes);
    url.searchParams.set("client_id", facebookClientId);
    url.searchParams.set("redirect_uri", redirectUri);
    url.searchParams.set("state", connectionAttemptId);

    return dataResult(url.toString());
}
// #endregion

// #region Amazon Marketplace
export const amazonSellerRegionUrlMap: Map<string, string> = new Map([
    ["NA", "https://sellercentral.amazon.com/apps/authorize/consent"],
    ["EU", "https://sellercentral-europe.amazon.com/apps/authorize/consent"],
    ["FE", "https://sellercentral-fe.amazon.com/apps/authorize/consent"],
]);

export const amazonVendorRegionUrlMap: Map<string, string> = new Map([
    ["NA", "https://vendorcentral.amazon.com/apps/authorize/consent"],
    ["EU", "https://vendorcentral.amazon.co.uk/apps/authorize/consent"],
    ["FE", "https://vendorcentral.amazon.co.jp/apps/authorize/consent"],
]);

export enum AmazonMarketplaceRegion {
    NA = "NA",
    EU = "EU",
    FE = "FE",
}

export function getAmazonMarketplaceOAuthRedirectUri(websiteBaseUrl: string) {
    const redirectUri = `${websiteBaseUrl}/oauth-callback/${ThirdPartyPlatformIds.amazonMarketplace}`;
    return redirectUri;
}

export function getAmazonMarketplaceOAuthUrl({
    connectionAttemptId,
    connectionStrategyId,
    region,
    scopes,
    websiteBaseUrl,
}: {
    connectionAttemptId: Uuid;
    connectionStrategyId: DAG_DataSourceConnectionStrategy;
    region: AmazonMarketplaceRegion;
    scopes: string;
    websiteBaseUrl: string;
}): Result<string> {
    // TODO: Get this from hades
    const amazonMarketplaceApplicationIdResult = zod.string().safeParse(import.meta.env.AMAZON_SP_APPLICATION_ID);
    const amazonMarketplaceOAuthUrlSuffixResult = zod.string().safeParse(import.meta.env.AMAZON_SP_OAUTH_URL_SUFFIX);
    if (amazonMarketplaceApplicationIdResult.success === false || amazonMarketplaceOAuthUrlSuffixResult.success === false) {
        return errorResult(new Error(uniqueUuid`27c2a03a-517a-4f58-a495-2ab1ce1cb106`));
    }
    const amazonMarketplaceApplicationId = amazonMarketplaceApplicationIdResult.data;
    const amazonMarketplaceOAuthUrlSuffix = amazonMarketplaceOAuthUrlSuffixResult.data;

    const redirectUri = getAmazonMarketplaceOAuthRedirectUri(websiteBaseUrl);
    let urlString: string;
    if (connectionStrategyId === DAG_DataSourceConnectionStrategy.amazonMarketplaceSeller) {
        urlString = amazonSellerRegionUrlMap.get(region)!;
    } else if (connectionStrategyId === DAG_DataSourceConnectionStrategy.amazonMarketplaceVendor) {
        urlString = amazonVendorRegionUrlMap.get(region)!;
    } else {
        return errorResult(new Error(uniqueUuid`04b039ea-ecd5-4fd6-96e5-89475360a31e`));
    }
    const url = new URL(urlString);
    url.searchParams.set("application_id", amazonMarketplaceApplicationId);
    url.searchParams.set("redirect_uri", redirectUri);
    url.searchParams.set("state", connectionAttemptId);

    return dataResult(url.toString() + amazonMarketplaceOAuthUrlSuffix);
}
// #endregion

// #region Amazon
export const AmazonRegionUrlMap: Map<string, string> = new Map([
    ["NA", "https://www.amazon.com/ap/oa"],
    ["EU", "https://eu.account.amazon.com/ap/oa"],
    ["FE", "https://apac.account.amazon.com/ap/oa"],
]);

export enum AmazonRegion {
    NA = "NA",
    EU = "EU",
    FE = "FE",
}

export function getAmazonOAuthRedirectUri(websiteBaseUrl: string) {
    const redirectUri = `${websiteBaseUrl}/oauth-callback/${ThirdPartyPlatformIds.amazon}`;

    return redirectUri;
}

export function getAmazonOAuthUrl({
    connectionAttemptId,
    region,
    scopes,
    websiteBaseUrl,
}: {
    connectionAttemptId: Uuid;
    region: AmazonRegion;
    scopes: string;
    websiteBaseUrl: string;
}): Result<string> {
    // TODO: Get this from hades
    const amazonClientIdResult = zod.string().safeParse(import.meta.env.AMAZON_CLIENT_ID);
    if (amazonClientIdResult.success === false) {
        return errorResult(new Error(uniqueUuid`421c415a-48a6-4839-8134-d7cc35b73aa6`));
    }
    const amazonClientId = amazonClientIdResult.data;

    const redirectUri = getAmazonOAuthRedirectUri(websiteBaseUrl);
    const url = new URL(`${AmazonRegionUrlMap.get(region)}`);
    url.searchParams.set("client_id", amazonClientId);
    url.searchParams.set("scope", scopes);
    url.searchParams.set("response_type", "code");
    url.searchParams.set("redirect_uri", redirectUri);
    url.searchParams.set("state", connectionAttemptId);

    return dataResult(url.toString());
}
// #endregion

// #region LinkedIn
export function getLinkedInOAuthRedirectUri(websiteBaseUrl: string) {
    const redirectUri = `${websiteBaseUrl}/oauth-callback/${ThirdPartyPlatformIds.linkedIn}`;
    return redirectUri;
}

export function getLinkedInOAuthUrl({
    connectionAttemptId,
    connectionStrategy,
    scopes,
    websiteBaseUrl,
}: {
    connectionAttemptId: Uuid;
    connectionStrategy: DAG_DataSourceConnectionStrategy;
    scopes: string;
    websiteBaseUrl: string;
}): Result<string> {
    // TODO: Get this from hades
    let linkedinClientIdResult;
    if (connectionStrategy === DAG_DataSourceConnectionStrategy.linkedinPage) {
        linkedinClientIdResult = zod.string().safeParse(import.meta.env.LINKEDIN_PAGE_CLIENT_ID);
    } else if (connectionStrategy === DAG_DataSourceConnectionStrategy.linkedinAds) {
        linkedinClientIdResult = zod.string().safeParse(import.meta.env.LINKEDIN_ADS_CLIENT_ID);
    } else {
        // TODO: throw some error
        return errorResult(new Error(uniqueUuid`d630b373-9f82-4256-a91f-30c1ebd4005b`));
    }
    const linkedinOauthVersionResult = zod.string().safeParse(import.meta.env.LINKEDIN_OAUTH_VERSION);
    if (linkedinClientIdResult.success === false || linkedinOauthVersionResult.success === false) {
        return errorResult(new Error(uniqueUuid`7e807bcb-47a4-45a6-874e-d0e21f623acd`));
    }
    const linkedInClientId = linkedinClientIdResult.data;
    const linkedInOauthVersion = linkedinOauthVersionResult.data;

    const redirectUri = getLinkedInOAuthRedirectUri(websiteBaseUrl);
    const url = new URL(`https://www.linkedin.com/oauth/${linkedInOauthVersion}/authorization`);
    url.searchParams.set("client_id", linkedInClientId);
    url.searchParams.set("scope", scopes);
    url.searchParams.set("response_type", "code");
    url.searchParams.set("redirect_uri", redirectUri);
    url.searchParams.set("state", connectionAttemptId);

    return dataResult(url.toString());
}
// #endregion
